import React, { useState, useEffect } from 'react';
import LayerRecommendation from './weather/LayerRecommendation';
import TemperatureDisplay from './weather/TemperatureDisplay';
import WeatherAlerts from './weather/WeatherAlerts';
import TemperatureUnitToggle from './TemperatureUnitToggle';
import EmojiVoting from './EmojiVoting';
import { feedbackScheduler } from '../lib/feedbackScheduler';
import { submitLayerFeedback } from '../lib/personalizedWeatherApi';

const WeatherDisplay = ({ 
  data, 
  location, 
  coordinates, 
  forecastHours, 
  alertForecastHours, 
  onRecommendationShown,
  isPersonalized = false,
  activityMode = 'walk'
}) => {
  const [showFeedback, setShowFeedback] = useState(false);
  const [currentVote, setCurrentVote] = useState(null);
  const [feedbackSessionId, setFeedbackSessionId] = useState(null);

  const displayLocation = location 
    ? location.split(' ').map(word => word.charAt(0).toUpperCase() + word.slice(1)).join(' ') 
    : (coordinates ? 'Your location' : 'Unknown location');

  // Call increment when recommendation is shown
  React.useEffect(() => {
    if (data?.recommendation && onRecommendationShown) {
      onRecommendationShown();
    }
  }, [data?.recommendation, onRecommendationShown]);

  // Check if feedback should be shown for personalized users
  useEffect(() => {
    if (isPersonalized && data?.recommendation && coordinates) {
      const shouldShow = feedbackScheduler.shouldShowFeedback();
      setShowFeedback(shouldShow);
      
      if (shouldShow) {
        // Generate unique session ID for this feedback request
        const sessionId = `feedback_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
        setFeedbackSessionId(sessionId);
      }
    }
  }, [isPersonalized, data?.recommendation, coordinates]);

  const handleVote = async (vote) => {
    if (!coordinates || !data) return;

    try {
      await submitLayerFeedback({
        vote,
        coordinates,
        weather: data,
        recommendation: data.recommendation,
        activityMode,
        forecastHours
      });

      // Record feedback in scheduler
      feedbackScheduler.recordFeedbackSubmitted(vote, {
        activityMode,
        forecastHours,
        sessionId: feedbackSessionId,
        location
      });

      setCurrentVote(vote);
      
      // Hide feedback after 3 seconds
      setTimeout(() => {
        setShowFeedback(false);
      }, 3000);

    } catch (error) {
      console.error('Failed to submit feedback:', error);
      throw error; // Let EmojiVoting component handle the error display
    }
  };

  return (
    <div className="space-y-3 relative">
      {data.recommendation && (
        <LayerRecommendation recommendation={data.recommendation} />
      )}
      
      <h3 className="text-2xl font-light text-gray-700 dark:text-gray-300">
        Weather in {displayLocation}
      </h3>

      <div className="relative">
        <div className="flex items-start justify-between">
          <TemperatureDisplay
            temperature={data.temperature.display}
            forecastHours={forecastHours}
            minTemp={data.temperature.dailyLow}
            maxTemp={data.temperature.dailyHigh}
          />
          <TemperatureUnitToggle />
        </div>
      </div>

      <WeatherAlerts
        isRainExpected={data.conditions.precipitation > 0}
        isSnowExpected={data.conditions.precipitation > 0 && data.temperature.actual < 0}
        forecastHours={forecastHours}
        alertForecastHours={alertForecastHours}
        windSpeed={data.conditions.windSpeed}
      />

      {/* Feedback component - only show in personalized mode */}
      {isPersonalized && showFeedback && (
        <EmojiVoting 
          onVote={handleVote}
          currentVote={currentVote}
          className="mt-4"
        />
      )}
    </div>
  );
};

export default WeatherDisplay;